import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { theme } from "../styles/theme";
import { Link } from "react-router-dom";
import img1 from "../assets/img1.jpeg";
import img2 from "../assets/img2.jpeg";
import img3 from "../assets/img3.jpeg";

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    ${theme.colors.primary},
    ${theme.colors.secondary}
  );
  position: relative;
  overflow: hidden;
  padding-top: 80px;
  perspective: 1000px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      text-align: center;
    }
  }
`;

const HeroContent = styled.div`
  flex: 1;
  color: ${theme.colors.white};
  z-index: 3;

  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    position: relative;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    background: linear-gradient(90deg, #ffffff, #caf0f8);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: glow 2s ease-in-out infinite alternate;

    @keyframes glow {
      from {
        text-shadow: 0 0 5px #fff, 0 0 10px #fff, 0 0 15px #00b4d8;
      }
      to {
        text-shadow: 0 0 10px #fff, 0 0 20px #00b4d8, 0 0 30px #00b4d8;
      }
    }

    @media (max-width: ${theme.breakpoints.tablet}) {
      font-size: 2.5rem;
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    max-width: 600px;
    line-height: 1.6;
    position: relative;

    @media (max-width: ${theme.breakpoints.tablet}) {
      font-size: 1.1rem;
      margin: 0 auto 2rem;
    }
  }

  .hero-buttons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: ${theme.breakpoints.tablet}) {
      justify-content: center;
    }
  }
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 600px;
  transform-style: preserve-3d;

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-top: 2rem;
    height: 400px;
  }

  .phone-container {
    position: relative;
    width: 300px;
    height: 600px;
    transform-style: preserve-3d;
    transform: rotateY(${(props) => props.rotateY}deg)
      rotateX(${(props) => props.rotateX}deg);
    transition: transform 0.5s ease;

    @media (max-width: ${theme.breakpoints.tablet}) {
      width: 240px;
      height: 480px;
    }
  }

  .phone-mockup {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 30px;
    border: 10px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    transform-style: preserve-3d;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0) 50%
      );
      border-radius: 30px;
      z-index: 2;
    }
  }

  .phone-frame {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 30px;
    border: 10px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
    z-index: 1;
  }

  .app-screen {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${theme.colors.white};
    border-radius: 20px;
    overflow: hidden;
    transform: translateZ(10px);
  }

  .app-header {
    background: linear-gradient(
      135deg,
      ${theme.colors.primary},
      ${theme.colors.secondary}
    );
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1.2rem;
  }

  .app-content {
    padding: 1.5rem;
    height: calc(100% - 70px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .app-logo {
    width: 80px;
    height: 80px;
    background: linear-gradient(
      135deg,
      ${theme.colors.primary},
      ${theme.colors.secondary}
    );
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    color: ${theme.colors.white};
    font-size: 2rem;
    box-shadow: 0 10px 20px rgba(0, 119, 182, 0.3);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 119, 182, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 119, 182, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 119, 182, 0);
    }
  }

  .floating-elements {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .floating-element {
    position: absolute;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(5px);
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .element-1 {
    width: 60px;
    height: 60px;
    top: 20%;
    left: 10%;
    animation: float 8s ease-in-out infinite;
  }

  .element-2 {
    width: 40px;
    height: 40px;
    top: 60%;
    left: 80%;
    animation: float 6s ease-in-out infinite 1s;
  }

  .element-3 {
    width: 80px;
    height: 80px;
    top: 80%;
    left: 20%;
    animation: float 7s ease-in-out infinite 2s;
  }

  .element-4 {
    width: 30px;
    height: 30px;
    top: 30%;
    left: 70%;
    animation: float 5s ease-in-out infinite 1.5s;
  }

  @keyframes float {
    0% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-20px) rotate(180deg);
    }
    100% {
      transform: translateY(0px) rotate(360deg);
    }
  }

  .glow-orb {
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 180, 216, 0.4) 0%,
      rgba(0, 180, 216, 0) 70%
    );
    filter: blur(20px);
    z-index: -1;
    animation: pulse-glow 4s ease-in-out infinite alternate;
  }

  .orb-1 {
    top: -100px;
    right: -100px;
  }

  .orb-2 {
    bottom: -100px;
    left: -100px;
    background: radial-gradient(
      circle,
      rgba(0, 119, 182, 0.4) 0%,
      rgba(0, 119, 182, 0) 70%
    );
    animation-delay: 2s;
  }

  @keyframes pulse-glow {
    0% {
      opacity: 0.5;
      transform: scale(1);
    }
    100% {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
    z-index: -1;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background-color: ${theme.colors.white};
    color: ${theme.colors.primary};

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }

  &.secondary {
    background-color: transparent;
    color: ${theme.colors.white};
    border: 2px solid ${theme.colors.white};

    &:hover {
      background-color: ${theme.colors.white};
      color: ${theme.colors.primary};
    }
  }
`;

const AppScreens = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  pointer-events: none;
`;

const AppScreen = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.white};
  border-radius: 20px;
  overflow: hidden;
  backface-visibility: hidden;
  transform: rotateY(${(props) => props.rotation}deg) translateZ(150px);
  opacity: ${(props) => (props.active ? 1 : 0)};
  transition: opacity 0.5s ease;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
`;

const ParticleCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const Hero = () => {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [activeScreen, setActiveScreen] = useState(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * -20;
      setRotateX(y);
      setRotateY(x);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles
    const createParticles = () => {
      particlesRef.current = [];
      const particleCount = 50;

      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 0.5 - 0.25,
          speedY: Math.random() * 0.5 - 0.25,
          opacity: Math.random() * 0.5 + 0.2,
        });
      }
    };

    const updateParticles = () => {
      particlesRef.current.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Wrap around edges
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.y > canvas.height) particle.y = 0;
        if (particle.y < 0) particle.y = canvas.height;
      });
    };

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((particle) => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        ctx.fill();
      });

      // Draw connections
      particlesRef.current.forEach((particle1, i) => {
        particlesRef.current.slice(i + 1).forEach((particle2) => {
          const distance = Math.sqrt(
            Math.pow(particle1.x - particle2.x, 2) +
              Math.pow(particle1.y - particle2.y, 2)
          );

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle1.x, particle1.y);
            ctx.lineTo(particle2.x, particle2.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${
              0.2 * (1 - distance / 100)
            })`;
            ctx.stroke();
          }
        });
      });
    };

    const animate = () => {
      updateParticles();
      drawParticles();
      animationRef.current = requestAnimationFrame(animate);
    };

    createParticles();
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      createParticles();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <HeroSection>
      <ParticleCanvas ref={canvasRef} />
      <div className="glow-orb orb-1"></div>
      <div className="glow-orb orb-2"></div>

      <div className="container">
        <HeroContent>
          <h1>Revolutionizing Fintech Solutions</h1>
          <p>
            AmpleLark Technologies develops cutting-edge applications that
            simplify everyday financial tasks. From rental management to booking
            services, we're transforming how people interact with technology.
          </p>
          <div className="hero-buttons">
            <Button to="/contact" className="primary">
              Get Started
            </Button>
            <Button to="#apps" className="secondary">
              Our Apps
            </Button>
          </div>
        </HeroContent>

        <HeroImage>
          <div className="floating-elements">
            <div className="floating-element element-1"></div>
            <div className="floating-element element-2"></div>
            <div className="floating-element element-3"></div>
            <div className="floating-element element-4"></div>
          </div>

          <div className="phone-container" rotateX={rotateX} rotateY={rotateY}>
            <div className="phone-frame"></div>
            <div className="phone-mockup">
              <AppScreens>
                <AppScreen rotation={0} active={activeScreen === 0}>
                  <img src={img1} alt="img1" />
                </AppScreen>

                <AppScreen rotation={0} active={activeScreen === 1}>
                  <img src={img3} alt="img2" />
                </AppScreen>

                <AppScreen rotation={0} active={activeScreen === 2}>
                  <img src={img2} alt="img3" />
                </AppScreen>
              </AppScreens>
            </div>
          </div>
        </HeroImage>
      </div>
    </HeroSection>
  );
};

export default Hero;
